<template>
    <v-dialog
        v-model="show"
        max-width="500"
    >
        <v-card>
            <div v-if="isVideo(file)" class="file-dialog-video-container">
                <video
                    :key="file.key"
                    ref="fileDialogVideo"
                    preload="metadata"
                    muted
                    loop
                    @mouseenter="playDialogVideo"
                    @mouseleave="pauseDialogVideo"
                >
                    <source :src="getFileUrl(file)" type="video/mp4">
                </video>
            </div>
            <div v-else-if="isPdf(file)" class="pdf-preview">
                <v-icon x-large color="#ef5350">mdi-file-pdf-box</v-icon>
            </div>
            <v-img v-else :src="$api.getImageUrl(file)" width="500" height="500" contain ref="image" @load="imageLoaded"/>

            <v-card-text class="mt-2">
                <span v-if="width && height">{{$translate('Size')}}: {{width}} x {{height}} px, {{humanFileSize}}<br></span>
                <span v-else-if="file && file.size">{{$translate('Size')}}: {{humanFileSize}}<br></span>
                <v-text-field
                    v-model="editBaseName"
                    :label="$translate('Filename')"
                    :suffix="fileExtension"
                    :error="!editBaseName.trim()"
                    :error-messages="!editBaseName.trim() ? [$translate('Filename cannot be empty')] : []"
                    dense
                    outlined
                    class="mt-2 mb-2"
                    @keydown.enter="save"
                />
                <div class="d-flex align-center mt-2 mb-2" style="gap: 8px;">
                    <v-select
                        v-model="editTags"
                        :items="availableTags"
                        item-text="name"
                        item-value="id"
                        :label="$translate('Tags')"
                        multiple
                        small-chips
                        deletable-chips
                        dense
                        outlined
                        hide-details
                        return-object
                        style="flex: 1;"
                    />
                    <v-btn small outlined color="primary" @click="showNewTag = true" style="flex-shrink: 0;">
                        {{ $translate('Create new') }}
                    </v-btn>
                </div>
                <div v-if="showNewTag" class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-text-field
                        v-model="newTagName"
                        :label="$translate('Tag name')"
                        dense
                        outlined
                        hide-details
                        autofocus
                        @keydown.enter="addNewTag"
                    />
                    <v-btn
                        small
                        color="primary"
                        :disabled="!newTagName.trim()"
                        :loading="addingTag"
                        @click="addNewTag"
                    >
                        {{ $translate('Add') }}
                    </v-btn>
                    <v-btn small text @click="showNewTag = false; newTagName = ''">
                        {{ $translate('Cancel') }}
                    </v-btn>
                </div>
                {{$translate('Created')}}: {{ file.created }}<br>
                {{$translate('Content type')}}: {{ file.content_type }}<br>
                <div class="d-flex align-center mt-2" style="gap: 8px;">
                    <v-text-field
                        :value="fileUrl"
                        :label="$translate('URL')"
                        dense
                        outlined
                        hide-details
                        readonly
                        @focus="$event.target.select()"
                    />
                    <v-btn small outlined @click="copyUrl">
                        <v-icon small left>mdi-content-copy</v-icon>
                        {{ $translate('Copy') }}
                    </v-btn>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-btn v-if="hasChanges" color="primary" text @click="save" :loading="saving" :disabled="!editBaseName.trim()">{{$translate('Save')}}</v-btn>
                <v-spacer></v-spacer>
                <v-btn @click="close">{{$translate('Close')}}</v-btn>
            </v-card-actions>
        </v-card>

    </v-dialog>
</template>

<script>
export default {
    name: 'FileDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        file: {
            type: Object,
            default: null
        }
    },
    data: () => ({
        width: null,
        height: null,
        editBaseName: '',
        editTags: [],
        availableTags: [],
        newTagName: '',
        showNewTag: false,
        addingTag: false,
        saving: false
    }),
    computed: {
        show: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        fileExtension() {
            if (!this.file || !this.file.name) return '';
            let idx = this.file.name.lastIndexOf('.');
            return idx !== -1 ? this.file.name.substring(idx) : '';
        },
        editName() {
            return this.editBaseName + this.fileExtension;
        },
        nameChanged() {
            return this.file && this.editName !== this.file.name;
        },
        tagsChanged() {
            if (!this.file) return false;
            let originalIds = (this.file.tags || []).map(t => t.id).sort().join(',');
            let editIds = this.editTags.map(t => t.id).sort().join(',');
            return originalIds !== editIds;
        },
        hasChanges() {
            return this.nameChanged || this.tagsChanged;
        },
        fileUrl() {
            if (!this.file) return '';
            const hostname = this.$root.siteconfig?.hostname || location.hostname;
            return `${location.protocol}//${hostname}/api/utils/file/download?key=${this.file.key}`;
        },
        humanFileSize() {
            if (!this.file) {
                return '';
            }

            let size = this.file.size;
            if (size < 1024) {
                return `${size} bytes`;
            } else if (size < 1024*1024) {
                return `${(size/1024).toFixed(0)} kB`.replace('.', ',');
            } else {
                return `${(size/(1024*1024)).toFixed(1)} MB`.replace('.', ',');
            }
        }
    },
    methods: {
        getFileUrl(file) {
            return this.$api.getImageUrl(file, null, {notrack: true});
        },
        isVideo(file) {
            return file && file.content_type && file.content_type.startsWith('video/');
        },
        isPdf(file) {
            return file && file.content_type === 'application/pdf';
        },
        playDialogVideo() {
            const video = this.$refs.fileDialogVideo;
            if (video) {
                video.play();
            }
        },
        pauseDialogVideo() {
            const video = this.$refs.fileDialogVideo;
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        },
        imageLoaded() {
            let image = this.$refs.image.image;
            this.width = image.naturalWidth
            this.height = image.naturalHeight
        },
        async addNewTag() {
            let name = this.newTagName.trim();
            if (!name || this.addingTag) return;
            this.addingTag = true;
            try {
                let response = await this.$api.post('/api/systemtag/add', {name});
                let data = await response.json();
                if (response.ok && data.id) {
                    this.availableTags.push(data);
                    this.editTags.push(data);
                    this.newTagName = '';
                    this.showNewTag = false;
                } else {
                    this.$store.commit('showErrorDialog', {
                        title: this.$translate('Failed'),
                        text: data.message ? this.$translate(data.message) : ''
                    });
                }
            } catch (e) {
                this.$store.commit('showErrorDialog', { title: this.$translate('Failed') });
            }
            this.addingTag = false;
        },
        async save() {
            if (!this.hasChanges || this.saving || !this.editBaseName.trim()) return;
            this.saving = true;
            try {
                let payload = {key: this.file.key};
                if (this.nameChanged) {
                    payload.name = this.editName;
                }
                if (this.tagsChanged) {
                    payload.tags = this.editTags.map(t => t.id);
                }
                let response = await this.$api.post('/api/utils/file/set_prop', payload);
                let data = await response.json();
                if (response.ok) {
                    if (this.nameChanged) {
                        this.file.name = this.editName;
                    }
                    this.file.tags = this.editTags.slice();
                    this.$emit('updated', this.file);
                } else {
                    this.$store.commit('showErrorDialog', {
                        title: this.$translate('Failed'),
                        text: data.message ? this.$translate(data.message) : ''
                    });
                }
            } catch (e) {
                this.$store.commit('showErrorDialog', { title: this.$translate('Failed') });
            }
            this.saving = false;
        },
        copyUrl() {
            navigator.clipboard.writeText(this.fileUrl);
        },
        loadTags() {
            this.$api.get('/api/systemtag/get').then(data => {
                this.availableTags = data;
            });
        },
        close() {
            this.show = false;
        }
    },
    watch: {
        show(val) {
            if (!val) {
                this.width = null;
                this.height = null;
                this.showNewTag = false;
                this.newTagName = '';
                this.addingTag = false;
                this.saving = false;
            } else {
                this.loadTags();
            }
        },
        file(val) {
            if (val && val.name) {
                let idx = val.name.lastIndexOf('.');
                this.editBaseName = idx !== -1 ? val.name.substring(0, idx) : val.name;
            } else {
                this.editBaseName = '';
            }
            this.editTags = val && val.tags ? val.tags.slice() : [];
        }
    }
}
</script>

<style scoped>
.file-dialog-video-container {
    width: 100%;
    margin: 0 auto;
}

.file-dialog-video-container video {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
}

.pdf-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    background: #f5f5f5;
}
</style>
